'use client';

import { Link } from '@curology/ui-components-web/react/Link';
import Image from 'next/image';
import { useMemo, useState } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { AddRxCard } from '@/components/product-selection/components/ExploreRxProducts/components/addRxCard';
import { SpeechHighlight } from '@/components/product-selection/components/ExploreRxProducts/components/speechHighlight';
import { SwitchRxCard } from '@/components/product-selection/components/ExploreRxProducts/components/switchRxCard';
import { useSelectedItemIds } from '@/components/product-selection/hooks/useSelectedItemIds';
import { Typography } from '@/components/squeeze';
import { DATA_KEYS } from '@/consts/dataKeys';
import { isDefined } from '@/utils/isDefined';

const AGENCY_PROVIDER_STICKER_SRC =
  'https://images.ctfassets.net/8pzh4g8cs30t/1lBGAuqwMIvPU1JiwIYchW/ed148ac510ab87baa6fa4a03ad193d95/Avatar-2.png';

export function ExploreRxProducts() {
  const [showOtherRxProducts, setShowOtherRxProducts] = useState(false);
  const { rxCards, addOnRxCards, flags } = useDataProvider(
    DATA_KEYS.productSelection,
  );
  const { brand } = useFlowContext();
  const { isCardSelected, findCard } = useSelectedItemIds();

  const rxCard = useMemo(() => {
    const selectedRx = rxCards.find((cardId) => isCardSelected(cardId));

    return selectedRx ? findCard(selectedRx) : undefined;
  }, [rxCards, findCard, isCardSelected]);

  const alternateRxCards = useMemo(() => {
    const applicableCards = flags.agencyMultiRx
      ? addOnRxCards
      : rxCards.filter((cardId) => !isCardSelected(cardId));

    return applicableCards.map((cardId) => findCard(cardId)).filter(isDefined);
  }, [rxCards, addOnRxCards, findCard, isCardSelected, flags.agencyMultiRx]);

  // With multi rx, lets display this component even if no rx cards are selected
  // Never show component if there are no cards to swap/add
  if (alternateRxCards.length === 0 || (!rxCard && !flags.agencyMultiRx)) {
    return null;
  }

  return flags.agencyMultiRx ? (
    <div className="cur-flex cur-flex-col cur-gap-2 sm:cur-px-4">
      {!flags.displayAdditionalRx && (
        <SpeechHighlight
          // TODO: Remove conditional when we have a speech bubble asset for Curology brand highlight
          speakerImage={
            brand === 'agency' && (
              <Image
                src={AGENCY_PROVIDER_STICKER_SRC}
                alt="Provider in white lab coat"
                width="48"
                height="48"
              />
            )
          }
        >
          <Typography size="body">
            We offer other customized Rx formulas you can add in for your trial
            offer.
          </Typography>
          <Link
            className="cur-decoration-[#2d2926]"
            href=""
            onClick={(event) => {
              event.preventDefault();
              setShowOtherRxProducts((shown) => !shown);
            }}
          >
            {showOtherRxProducts
              ? 'Hide other products'
              : 'Explore other products'}
          </Link>
        </SpeechHighlight>
      )}
      {(showOtherRxProducts || flags.displayAdditionalRx) &&
        alternateRxCards.map((addOnRxCard) => (
          <AddRxCard
            key={`switch-rx-alternate-${addOnRxCard.id}`}
            alternateRxCard={addOnRxCard}
            isSelected={isCardSelected(addOnRxCard)}
          />
        ))}
    </div>
  ) : (
    <div className="cur-flex cur-flex-col cur-gap-2 sm:cur-px-4">
      <SpeechHighlight
        // TODO: Remove conditional when we have a speech bubble asset for Curology brand highlight
        speakerImage={
          brand === 'agency' && (
            <Image
              src={AGENCY_PROVIDER_STICKER_SRC}
              alt="Provider in white lab coat"
              width="48"
              height="48"
            />
          )
        }
      >
        <Typography size="body">
          We offer other customized Rx formulas you can swap in for your trial
          offer.
        </Typography>
        <Link
          className="cur-decoration-[#2d2926]"
          href=""
          onClick={(event) => {
            event.preventDefault();
            setShowOtherRxProducts((shown) => !shown);
          }}
        >
          {showOtherRxProducts
            ? 'Hide other products'
            : 'Explore other products'}
        </Link>
      </SpeechHighlight>
      {showOtherRxProducts &&
        rxCard &&
        alternateRxCards.map((alternateRxCard) => (
          <SwitchRxCard
            key={`switch-rx-alternate-${alternateRxCard.id}`}
            rxCard={rxCard}
            alternateRxCard={alternateRxCard}
            onSuccess={() => setShowOtherRxProducts(false)}
          />
        ))}
    </div>
  );
}
